import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { EntityFormDialog } from '@/shared/components';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, MapPin, Building2, Tag, Trash2, History, MapPinned, Plus, ChevronUp } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { MaskedInput } from '@/components/ui/masked-input';
import { useSession } from '@/hooks/useSession';
import type { Site, UpdateSiteInput } from '../../types';
import { SITE_TAGS } from '../../types';
import { TagInput } from '../TagInput';
import { GpsPickerModal } from '../GpsPickerModal';
import { SiteChangelog } from '../SiteChangelog';
import { useSiteChangelog, useUsergroupAdmins, useCreateLocationForSite } from '../../hooks';
import { DualListBox } from '@/components/ui/dual-listbox';
import { useSiteWithOptions } from '../../hooks';
import type { LocationOption, UsergroupOption } from '../../types';

interface SiteFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  site: Site | null;
  onSubmit: (data: UpdateSiteInput) => Promise<void>;
  onDelete?: (siteId: string) => Promise<void>;
  isSubmitting: boolean;
  isDeleting?: boolean;
  canDelete?: boolean;
  simpleForm?: boolean;
}

/**
 * Get account name from site (V4 has accountName directly)
 */
function getAccountName(site: Site): string {
  if (site.accountName) return site.accountName;
  return '—';
}

export function SiteFormModal({
  open,
  onOpenChange,
  site,
  onSubmit,
  onDelete,
  isSubmitting,
  isDeleting,
  canDelete = false,
  simpleForm = false,
}: SiteFormModalProps) {
  const { t } = useTranslation('sites');
  const { user: sessionUser } = useSession();
  const isFullAdmin = !!(sessionUser?.postgres_roles?.includes('full_admin') || sessionUser?.postgres_roles?.includes('director'));

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showGpsPicker, setShowGpsPicker] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [abbreviations, setAbbreviations] = useState<string[]>([]);
  const [phone, setPhone] = useState('');
  const [phoneExtension, setPhoneExtension] = useState('');
  const [fax, setFax] = useState('');
  const [faxExtension, setFaxExtension] = useState('');
  const [email, setEmail] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [stateProvince, setStateProvince] = useState('');
  const [country, setCountry] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [latitude, setLatitude] = useState<number | undefined>(undefined);
  const [longitude, setLongitude] = useState<number | undefined>(undefined);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedUsergroups, setSelectedUsergroups] = useState<string[]>([]);
  const [showQuickAddLocation, setShowQuickAddLocation] = useState(false);
  const [quickLocationName, setQuickLocationName] = useState('');
  const [quickLocationRoom, setQuickLocationRoom] = useState('');

  // Fetch changelog
  const siteId = site?.id || site?._id || site?.mongoSiteId;
  const changelogQuery = useSiteChangelog(open && !simpleForm ? siteId : undefined);

  // Fetch site options (locations, usergroups) from V3
  const siteOptionsQuery = useSiteWithOptions(open && !simpleForm ? siteId : undefined);

  // Fetch admins for selected usergroups (Gap 5)
  const adminsQuery = useUsergroupAdmins(selectedUsergroups);

  // Quick location creation mutation (Gap 8)
  const createLocationMutation = useCreateLocationForSite();

  // Reset form when site changes
  useEffect(() => {
    if (site) {
      setName(site.name || '');
      setAbbreviations(site.abbreviations || []);
      setPhone(site.phone || '');
      setPhoneExtension(site.phoneExtension || site.extension || '');
      setFax(site.fax || '');
      setFaxExtension(site.fax_extension || '');
      setEmail(site.email || '');
      setAddressLine1(site.addressLine1 || site.address || '');
      setCity(site.city || '');
      setPostalCode(site.postalCode || site.postcode || '');
      setStateProvince(site.stateProvince || site.state_province_county || '');
      setCountry(site.country || '');
      setDescription(site.description || '');
      setSelectedTags(site.tags || []);
      // V4: latitude/longitude, V3: geolocation array
      setLatitude(site.latitude ?? site.geolocation?.[0]);
      setLongitude(site.longitude ?? site.geolocation?.[1]);
      // Locations and usergroups from V3 data
      const locs = site.locations;
      if (Array.isArray(locs)) {
        setSelectedLocations(
          locs.map((l) => (typeof l === 'string' ? l : l._id))
        );
      } else {
        setSelectedLocations([]);
      }
      const ugs = site.usergroups;
      if (Array.isArray(ugs)) {
        setSelectedUsergroups(
          ugs.map((u) => (typeof u === 'string' ? u : u._id))
        );
      } else {
        setSelectedUsergroups([]);
      }
    } else {
      setName('');
      setAbbreviations([]);
      setPhone('');
      setPhoneExtension('');
      setFax('');
      setFaxExtension('');
      setEmail('');
      setAddressLine1('');
      setCity('');
      setPostalCode('');
      setStateProvince('');
      setCountry('');
      setDescription('');
      setSelectedTags([]);
      setLatitude(undefined);
      setLongitude(undefined);
      setSelectedLocations([]);
      setSelectedUsergroups([]);
    }
  }, [site]);

  const isTransporteur = selectedTags.includes('transporteur');

  const handleTagToggle = (tag: string) => {
    setSelectedTags((prev) => {
      if (prev.includes(tag)) {
        return prev.filter((t) => t !== tag);
      }
      // If selecting transporteur, remove billing and shipping
      if (tag === 'transporteur') {
        return [...prev.filter((t) => t !== 'billing' && t !== 'shipping'), tag];
      }
      return [...prev, tag];
    });
  };

  const handleQuickAddLocation = async () => {
    if (!quickLocationName.trim() || !siteId || !site?.accountId) return;
    try {
      const newLoc = await createLocationMutation.mutateAsync({
        siteId,
        accountId: site.accountId,
        data: { name: quickLocationName.trim(), roomNumber: quickLocationRoom.trim() || undefined },
      });
      // V4 create returns id (MongoDB ObjectId as temp ID until CDC assigns UUID)
      setSelectedLocations((prev) => [...prev, newLoc.id]);
      setQuickLocationName('');
      setQuickLocationRoom('');
      setShowQuickAddLocation(false);
    } catch {
      // Error handled by mutation hook
    }
  };

  const handleSubmit = async () => {
    const data: UpdateSiteInput = {
      name: name.trim(),
      abbreviations,
      phone: phone.trim() || undefined,
      phoneExtension: phoneExtension.trim() || undefined,
      fax: fax.trim() || undefined,
      faxExtension: faxExtension.trim() || undefined,
      email: email.trim() || undefined,
      addressLine1: addressLine1.trim() || undefined,
      city: city.trim() || undefined,
      postalCode: postalCode.trim() || undefined,
      stateProvince: stateProvince.trim() || undefined,
      country: country.trim() || undefined,
      description: description.trim() || undefined,
      tags: selectedTags,
    };

    // Include coordinates if admin edited them
    if (isFullAdmin && latitude !== undefined && longitude !== undefined) {
      data.latitude = latitude;
      data.longitude = longitude;
    }

    // Include locations and usergroups
    if (selectedLocations.length > 0 || site?.locations) {
      (data as Record<string, unknown>).locations = selectedLocations;
    }
    if (selectedUsergroups.length > 0 || site?.usergroups) {
      (data as Record<string, unknown>).usergroups = selectedUsergroups;
    }

    await onSubmit(data);
  };

  const handleDelete = async () => {
    if (site && onDelete) {
      const id = site.id || site._id || site.mongoSiteId;
      if (!id) {
        toast.error('Cannot delete site: missing site ID');
        return;
      }
      await onDelete(id);
      setShowDeleteDialog(false);
      onOpenChange(false);
    }
  };

  const handleGpsConfirm = (lat: number, lng: number) => {
    setLatitude(lat);
    setLongitude(lng);
  };

  if (!site) return null;

  // Available options for DualListBox
  const availableLocations: LocationOption[] = siteOptionsQuery.data?.options?.locations || [];
  const availableUsergroups: UsergroupOption[] = siteOptionsQuery.data?.options?.usergroups || [];

  return (
    <>
      <EntityFormDialog
        open={open}
        onOpenChange={onOpenChange}
        size="2xl"
        title={t('edit.edit', 'Edit Site')}
        description={getAccountName(site)}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        saveDisabled={!name.trim()}
        saveLabel={t('edit.save', 'Save')}
        cancelLabel={t('edit.cancel', 'Cancel')}
        footerExtras={
          canDelete && onDelete ? (
            <Button
              type="button"
              variant="destructive"
              onClick={() => setShowDeleteDialog(true)}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Trash2 className="h-4 w-4 mr-1" />
              )}
              {t('edit.delete', 'Delete')}
            </Button>
          ) : undefined
        }
      >
            <Tabs defaultValue="details" className="w-full">
              <TabsList className={`grid w-full ${simpleForm ? 'grid-cols-2' : 'grid-cols-5'}`}>
                <TabsTrigger value="details" className="flex items-center gap-1">
                  <Building2 className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('tabs.details', 'Details')}</span>
                </TabsTrigger>
                <TabsTrigger value="address" className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('tabs.address', 'Address')}</span>
                </TabsTrigger>
                {!simpleForm && (
                  <TabsTrigger value="settings" className="flex items-center gap-1">
                    <Tag className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('tabs.settings', 'Settings')}</span>
                  </TabsTrigger>
                )}
                {!simpleForm && (
                  <TabsTrigger value="locations" className="flex items-center gap-1">
                    <MapPinned className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('tabs.locations', 'Locations')}</span>
                  </TabsTrigger>
                )}
                {!simpleForm && (
                  <TabsTrigger value="history" className="flex items-center gap-1">
                    <History className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('tabs.history', 'History')}</span>
                  </TabsTrigger>
                )}
              </TabsList>

              {/* Details Tab */}
              <TabsContent value="details" className="space-y-4 mt-4">
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="name">{t('edit.name', 'Name')} *</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label>{t('edit.abbreviations', 'Abbreviations')}</Label>
                    <TagInput
                      id="abbreviations"
                      value={abbreviations}
                      onChange={setAbbreviations}
                      placeholder="Type and press Enter"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="phone">{t('edit.phone', 'Phone')}</Label>
                      <MaskedInput
                        id="phone"
                        mask="(000) 000-0000"
                        value={phone}
                        onAccept={(val) => setPhone(val)}
                        placeholder="(___) ___-____"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="phoneExtension">Ext.</Label>
                      <Input
                        id="phoneExtension"
                        value={phoneExtension}
                        onChange={(e) => setPhoneExtension(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="fax">Fax</Label>
                      <MaskedInput
                        id="fax"
                        mask="(000) 000-0000"
                        value={fax}
                        onAccept={(val) => setFax(val)}
                        placeholder="(___) ___-____"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="faxExtension">Ext.</Label>
                      <Input
                        id="faxExtension"
                        value={faxExtension}
                        onChange={(e) => setFaxExtension(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="email">{t('edit.email', 'Email')}</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label>{t('edit.description', 'Description')}</Label>
                    <RichTextEditor
                      value={description}
                      onChange={setDescription}
                      placeholder="Enter description..."
                      minHeight="80px"
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Address Tab */}
              <TabsContent value="address" className="space-y-4 mt-4">
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="addressLine1">{t('edit.address', 'Address')}</Label>
                    <Input
                      id="addressLine1"
                      value={addressLine1}
                      onChange={(e) => setAddressLine1(e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="city">{t('edit.city', 'City')}</Label>
                      <Input
                        id="city"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="postalCode">{t('edit.postcode', 'Postcode')}</Label>
                      <Input
                        id="postalCode"
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="stateProvince">{t('edit.state_province_county', 'Province')}</Label>
                      <Input
                        id="stateProvince"
                        value={stateProvince}
                        onChange={(e) => setStateProvince(e.target.value)}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="country">{t('edit.country', 'Country')}</Label>
                      <Input
                        id="country"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* GPS Coordinates */}
                  <div className="grid gap-2">
                    <Label>{t('edit.geolocation', 'Geolocation')}</Label>
                    <div className="flex items-center gap-3">
                      <div className="grid grid-cols-2 gap-2 flex-1">
                        <Input
                          type="number"
                          step="any"
                          placeholder="Latitude"
                          value={latitude ?? ''}
                          onChange={(e) => setLatitude(e.target.value ? parseFloat(e.target.value) : undefined)}
                          disabled={!isFullAdmin}
                        />
                        <Input
                          type="number"
                          step="any"
                          placeholder="Longitude"
                          value={longitude ?? ''}
                          onChange={(e) => setLongitude(e.target.value ? parseFloat(e.target.value) : undefined)}
                          disabled={!isFullAdmin}
                        />
                      </div>
                      {isFullAdmin && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowGpsPicker(true)}
                        >
                          <MapPin className="h-4 w-4 mr-1" />
                          {t('form.pickOnMap', 'Map')}
                        </Button>
                      )}
                    </div>
                    {!isFullAdmin && latitude !== undefined && longitude !== undefined && (
                      <p className="text-xs text-muted-foreground">
                        {latitude.toFixed(6)}, {longitude.toFixed(6)}
                      </p>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Settings Tab */}
              {!simpleForm && (
              <TabsContent value="settings" className="space-y-4 mt-4">
                <div className="grid gap-4">
                  {/* Tags - only visible for full admin */}
                  {isFullAdmin && (
                    <div className="grid gap-2">
                      <Label>{t('edit.tags', 'Tags')}</Label>
                      <div className="flex flex-wrap gap-3">
                        {SITE_TAGS.map((tag) => {
                          const isDisabledByTransporteur =
                            isTransporteur && (tag === 'billing' || tag === 'shipping');
                          return (
                            <div key={tag} className="flex items-center space-x-2">
                              <Checkbox
                                id={`tag-${tag}`}
                                checked={selectedTags.includes(tag)}
                                onCheckedChange={() => handleTagToggle(tag)}
                                disabled={isDisabledByTransporteur}
                              />
                              <label
                                htmlFor={`tag-${tag}`}
                                className={`text-sm cursor-pointer ${
                                  isDisabledByTransporteur ? 'text-muted-foreground' : ''
                                }`}
                              >
                                {tag}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                      {isTransporteur && (
                        <p className="text-xs text-muted-foreground">
                          Billing and shipping tags are disabled when transporteur is selected.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Read-only info */}
                  <div className="grid gap-2">
                    <Label>{t('edit.account', 'Account')}</Label>
                    <p className="text-sm text-muted-foreground">
                      {getAccountName(site)}
                      {site.accountNumber && ` (${site.accountNumber})`}
                    </p>
                  </div>

                  {(site.createdAt || site.created_at) && (
                    <div className="grid gap-2">
                      <Label>{t('edit.created_at', 'Created At')}</Label>
                      <p className="text-sm text-muted-foreground">
                        {new Date(site.createdAt || site.created_at || '').toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>
              )}

              {/* Locations & Usergroups Tab */}
              {!simpleForm && (
              <TabsContent value="locations" className="space-y-4 mt-4">
                <div className="grid gap-6">
                  <div className="grid gap-2">
                    <div className="flex items-center justify-between">
                      <Label>{t('edit.locations', 'Locations')}</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowQuickAddLocation(!showQuickAddLocation)}
                      >
                        {showQuickAddLocation ? (
                          <ChevronUp className="h-3 w-3 mr-1" />
                        ) : (
                          <Plus className="h-3 w-3 mr-1" />
                        )}
                        {t('edit.quickAddLocation', 'Quick Add')}
                      </Button>
                    </div>

                    {/* Quick Add Location inline form (Gap 8) */}
                    {showQuickAddLocation && (
                      <div className="border rounded-md p-3 space-y-2 bg-muted/30">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">{t('edit.locationName', 'Name')} *</Label>
                            <Input
                              value={quickLocationName}
                              onChange={(e) => setQuickLocationName(e.target.value)}
                              placeholder="Location name"
                              className="h-8 text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">{t('edit.roomNumber', 'Room Number')}</Label>
                            <Input
                              value={quickLocationRoom}
                              onChange={(e) => setQuickLocationRoom(e.target.value)}
                              placeholder="Optional"
                              className="h-8 text-sm"
                            />
                          </div>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleQuickAddLocation}
                          disabled={!quickLocationName.trim() || createLocationMutation.isPending}
                        >
                          {createLocationMutation.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                          {t('edit.addLocation', 'Add Location')}
                        </Button>
                      </div>
                    )}

                    <DualListBox<LocationOption>
                      available={availableLocations}
                      selected={selectedLocations}
                      onSelectionChange={setSelectedLocations}
                      getItemId={(item) => item._id}
                      getItemLabel={(item) => item.name}
                      availableLabel={t('edit.available', 'Available')}
                      selectedLabel={t('edit.selected', 'Selected')}
                      searchPlaceholder={t('edit.searchLocations', 'Search locations...')}
                      listHeight={200}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label>{t('edit.usergroups', 'User Groups')}</Label>
                    <DualListBox<UsergroupOption>
                      available={availableUsergroups}
                      selected={selectedUsergroups}
                      onSelectionChange={setSelectedUsergroups}
                      getItemId={(item) => item._id}
                      getItemLabel={(item) => item.name}
                      availableLabel={t('edit.available', 'Available')}
                      selectedLabel={t('edit.selected', 'Selected')}
                      searchPlaceholder={t('edit.searchUsergroups', 'Search user groups...')}
                      listHeight={200}
                    />

                    {/* Admin Preview (Gap 5) */}
                    {selectedUsergroups.length > 0 && (
                      <div className="mt-2">
                        <Label className="text-xs text-muted-foreground">{t('edit.admins', 'Admins')}</Label>
                        {adminsQuery.isLoading ? (
                          <div className="flex items-center gap-2 py-1">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            <span className="text-xs text-muted-foreground">Loading admins...</span>
                          </div>
                        ) : adminsQuery.data && adminsQuery.data.length > 0 ? (
                          <ul className="text-sm text-muted-foreground mt-1 space-y-0.5">
                            {adminsQuery.data.map((admin) => (
                              <li key={admin._id}>
                                {admin.firstname} {admin.lastname}
                                {admin.email && <span className="text-xs ml-1">({admin.email})</span>}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-xs text-muted-foreground mt-1">No admins found</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
              )}

              {/* History Tab */}
              {!simpleForm && (
              <TabsContent value="history" className="mt-4">
                <SiteChangelog
                  changelog={changelogQuery.data || []}
                  isLoading={changelogQuery.isLoading}
                />
              </TabsContent>
              )}
            </Tabs>

      </EntityFormDialog>

      {/* GPS Picker Modal */}
      <GpsPickerModal
        open={showGpsPicker}
        onOpenChange={setShowGpsPicker}
        initialLat={latitude}
        initialLng={longitude}
        onConfirm={handleGpsConfirm}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('edit.sure', 'Delete site?')}</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the site &quot;{site?.name}&quot;.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('edit.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('edit.delete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
